import { initAxios } from '../../../utils/axiosHelper';
import {
  IBulkEntityRelationshipsPayload,
  IBulkEntityRelationshipsResult,
  ICreateEntityRelationship,
  IEntityRelationship,
  IEntityRelationshipWithNames,
} from './entityRelationships.d';

const getRelationshipRequestErrorMessage = (err: any, fallback: string) => {
  const responseData = err?.response?.data;
  if (responseData?.message) {
    return responseData.message;
  }

  const firstIssue = responseData?.errors?.[0];
  if (typeof firstIssue?.message === 'string' && firstIssue.message.trim()) {
    return firstIssue.message;
  }

  return fallback;
};

export const createEntityRelationship = async (
  payload: ICreateEntityRelationship,
  teamId?: string,
): Promise<{
  success: boolean;
  data?: IEntityRelationship;
  message?: string;
}> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return { success: false, message: 'Failed to initialize API client' };
    }

    const resp = await instance.post(
      '/bite-db/entity-relationship',
      JSON.stringify(payload),
      {
        headers: {
          'x-group-id': teamId || '',
        },
      },
    );

    if (resp?.data?.success) {
      return { success: true, data: resp.data?.data };
    }

    return {
      success: false,
      message: resp?.data?.message || 'Failed to create relationship',
    };
  } catch (err: any) {
    return {
      success: false,
      message: getRelationshipRequestErrorMessage(
        err,
        'Failed to create relationship',
      ),
    };
  }
};

export const deleteEntityRelationship = async (
  relationshipId: string,
): Promise<{
  success: boolean;
  data?: IEntityRelationship;
  message?: string;
}> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return { success: false, message: 'Failed to initialize API client' };
    }

    const resp = await instance.delete(
      `/bite-db/entity-relationship/${relationshipId}`,
    );

    if (resp?.data?.success) {
      return { success: true, data: resp.data?.data };
    }

    return {
      success: false,
      message: resp?.data?.message || 'Failed to remove relationship',
    };
  } catch (err: any) {
    return {
      success: false,
      message: getRelationshipRequestErrorMessage(
        err,
        'Failed to remove relationship',
      ),
    };
  }
};

export const createBulkEntityRelationships = async (
  payload: IBulkEntityRelationshipsPayload,
  teamId?: string,
): Promise<IBulkEntityRelationshipsResult> => {
  try {
    const instance = await initAxios();
    if (!instance) {
      return { success: false, message: 'Failed to initialize API client' };
    }

    const resp = await instance.post(
      '/bite-db/entity-relationships-bulk',
      JSON.stringify(payload),
      {
        headers: {
          'x-group-id': teamId || '',
        },
      },
    );

    return resp.data;
  } catch (err: any) {
    return {
      success: false,
      message: getRelationshipRequestErrorMessage(
        err,
        'Failed to create bulk relationships',
      ),
    };
  }
};

export const getEntityRelationships = async (
  entityId?: string,
  includeInactive = false,
): Promise<IEntityRelationshipWithNames[]> => {
  try {
    if (!entityId) {
      return [];
    }
    const instance = await initAxios();
    if (!instance) {
      return [];
    }

    const query = includeInactive ? '?includeInactive=true' : '';
    const resp = await instance.get(
      `/bite-db/entity/${entityId}/relationships${query}`,
    );
    return resp.data?.data || [];
  } catch (err) {
    return [];
  }
};
